import React, { useState, useContext, useRef } from "react"
import { FirebaseContext } from "../Firebase"
import { WingsModal } from "@wingspans-ui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faImages, faTrash as faTrashSolid } from "@fortawesome/free-solid-svg-icons"
import { faCheck } from "@fortawesome/pro-solid-svg-icons"
import { faTrash as faTrashRegular } from "@fortawesome/pro-regular-svg-icons"
import imageCompression from "browser-image-compression"
import { FormattedMessage } from "react-intl"
import ProfileImage from "../profile-image/profile-image"
import { deleteField } from "firebase/firestore"
import { defaultProfileImageColor } from "../profile-image/profile-image"
import { pickAccessibleTextColor } from "../../utils"

let fileReader
if (typeof window !== "undefined") {
  fileReader = new FileReader()
}

/**
 * Modal for editing the current user's profile image
 * @prop {boolean} hasText - whether the button to open the modal should contain
 * text or just be an icon
 * @returns {JSX}
 */
const ProfileImageModal = ({ hasText }) => {
  const { firebase, profile } = useContext(FirebaseContext)
  const [error, setError] = useState(null)
  // null represents no changes, "" represents deleting the image, "<url>" represents a change
  const [fileUrl, setFileUrl] = useState(null)
  const [fileBlob, setFileBlob] = useState(null)
  const [profileColor, setProfileColor] = useState("") // "" for no change, hex string if a color has been selected
  const [open, setOpen] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const imageFileInputRef = useRef(null)

  /**
   * Validate image parameters and generate thumbnail.
   *
   * @param {SyntheticEvent} e
   */
  const handleImageChange = async e => {
    e.persist()
    const file = e.target.files[0]

    setError(null)

    if (!file) {
      return
    }

    const allowedTypes = ["image/jpg", "image/jpeg", "image/png"]

    if (!allowedTypes.includes(file.type)) {
      setError("File must be one of jpg, jpeg, png")
      return
    }

    try {
      setIsCompressing(true)
      const compressedImage = await imageCompression(file, {
        maxSizeMB: 2,
      })

      // Triggered by readAsDataURL
      fileReader.onloadend = () => {
        setFileUrl(fileReader.result)
        setIsCompressing(false)

        // create image element in order to capture pixel dimensions
        const img = new Image()
        img.src = fileReader.result
      }

      fileReader.readAsDataURL(compressedImage)
      setFileBlob(compressedImage)
    } catch {
      setError("Something went wrong uploading your image")
    }
  }

  // Only display removal button if there's something to remove
  const enableRemoval = !isCompressing && (fileBlob || (profile?.profileImageUrl && fileUrl === null))

  const handleRemoveProfileImage = () => {
    if (isCompressing) {
      setError("Please wait for your file to finish loading.")
      return
    } else if (!enableRemoval) {
      setError("There is no profile image to remove")
      return
    }

    setFileUrl(profile.profileImageUrl ? "" : null)
    setFileBlob(null)
    setError(null)
    imageFileInputRef.current.value = "" // clear any locally uploaded images 
  }

  const handleClose = () => {
    setOpen(false)
    setFileUrl(null)
    setFileBlob(null)
    setProfileColor("")
  }

  const handleSubmit = async () => {
    if (isCompressing) {
      setError("Please wait for your file to finish loading.")
      return
    } else if (fileUrl === null && profileColor === "") {
      setError("You haven't made any modifications to save")
      return
    }

    try {
      if (fileUrl === null) {
        await firebase.updateProfile({ data: { profileColor: profileColor } })
      } else if (fileUrl === "") {
        const newProfileData = { profileImageUrl: deleteField() }
        if (profileColor) {
          newProfileData.profileColor = profileColor
        }
        await Promise.all([
          firebase.deleteProfileImage(),
          firebase.updateProfile({ data: newProfileData }),
        ])
      } else {
        const savedImageURL = await firebase.storeProfileImage(fileBlob)
        if (savedImageURL) {
          await firebase.updateProfile({ data: { profileImageUrl: savedImageURL } })
        }
      }
    } catch (e) {
      setError(
        "There was a problem updating your profile picture. Please try again later."
      )
    }
    
    handleClose()
  }

  // Allow background color selection if the current profile image preview is
  // completely loaded and doesn't contain an image
  const enableColorSelection = !isCompressing && !!profile && (fileUrl === "" || (fileUrl === null && !profile.profileImageUrl))
  const availableProfileColors = ["#C837D3", "#E07306", "#CAFE13", "#40777C", "#5ADEF6", "#0099D1", "#9381EE", "#5546DF", "#6F6F6F"]
  const currentProfileColor = profileColor || profile?.profileColor || defaultProfileImageColor

  return (
    <>
      <button
        className="open-portfolio-profile-image-modal-button"
        onClick={() => {
          setError(null)
          setOpen(true)
        }}
      >
        {hasText ? (
          <>
            <button className="button component-button component-button-ghost component-button-ghost-inactive component-button-small">
            <FormattedMessage id="portfolio-profile-image-change-profile-picture" defaultMessage="Change Profile Picture" />{" "}
            </button>
          </>
        ) : (
          <FontAwesomeIcon icon={faImages} />
        )}
      </button>
      <WingsModal
        isOpen={open}
        size="lg"
        onClose={handleClose}
        className="portfolio-profile-image-modal"
        headerAlign="center"
        title={<FormattedMessage id="portfolio-profile-image-change-profile-picture" defaultMessage="Change Profile Picture" />}
        titleLevel={5}
        secondaryActionLabel={<FormattedMessage id="dashboard-cancel" defaultMessage="Cancel" />}
        onSecondaryAction={handleClose}
        primaryActionLabel={<FormattedMessage id="dashboard-this-week-save-button" defaultMessage="Save" />}
        onPrimaryAction={handleSubmit}
      >
        <WingsModal.Body className="portfolio-profile-image-modal-body">
          <div>
            {/* <h5>
              <FormattedMessage id="portfolio-profile-image-preview-profile-picture" defaultMessage="Profile Picture Preview" />
            </h5> */}
            <div className="profile-image-and-remove-button-container">
              <div className="profile-image-preview-container">
                <ProfileImage 
                  profile={profile} 
                  potentialReplacementURL={fileUrl}
                  isLoading={isCompressing}
                  potentialReplacementColor={profileColor}
                />
                {enableRemoval && (
                  <button className="remove-profile-image-button" onClick={handleRemoveProfileImage}>
                    <FontAwesomeIcon icon={faTrashRegular} className="show-by-default" />
                    <FontAwesomeIcon icon={faTrashSolid} className="show-on-hover" />
                  </button>
                )}
              </div>
            </div>
            {enableColorSelection && (
              <div className="profile-image-color-selection-buttons-container">
                {availableProfileColors.map(color => (
                  <button
                    key={color}
                    className="profile-color-selection-button"
                    style={{backgroundColor: color, color: pickAccessibleTextColor(color)}}
                    onClick={() => setProfileColor(color)}
                  >
                    {color === currentProfileColor && (
                      <FontAwesomeIcon icon={faCheck} />
                    )}
                  </button>
                ))}
              </div>
            )}
            <div
              className="form-element portfolio-image-modal-upload-section"
              style={{borderColor: error ? "red" : ""}}
            >
              <label
                className="custom-file-input-label"
                htmlFor="profileImage"
              >
                <FormattedMessage id="portfolio-image-modal-choose-image" defaultMessage="Choose image" />
              </label>
              <input
                ref={imageFileInputRef}
                id="profileImage"
                name="profileImage"
                type="file"
                onChange={handleImageChange}
              />
              {error ? (
                <small className="profile-image-modal-error-message">{error}</small>
              ) : imageFileInputRef.current?.files.length ? (
                imageFileInputRef.current.files[0].name
              ) : (
                <span id="profileImageLabel">
                  <FormattedMessage id="portfolio-image-modal-no-image-chosen" defaultMessage="No image chosen" />
                </span>
              )}
            </div>
          </div>
        </WingsModal.Body>
      </WingsModal>
    </>
  )
}

export default ProfileImageModal
